// Android "Share → PlanA": the service worker stores what was shared, this page adds it to Spark.
import { useEffect, useRef, useState } from 'react';
import { api, type Me, type Project } from '../api.js';
import { createFromText, createImages, inSpark } from '../cards/create.js';
import { problemBanner } from '../components.js';
import { useProject } from '../doc/useProject.js';
import { href, navigate } from '../router.js';

interface Shared { title: string; text: string; url: string; files: File[] }

async function readShared(): Promise<Shared | null> {
  if (!('caches' in window)) return null;
  const cache = await caches.open('plana-share');
  const meta = await cache.match('/share-target/meta');
  if (!meta) return null;
  const data = (await meta.json()) as { title: string; text: string; url: string; files: { key: string; name: string; type: string }[] };
  const files: File[] = [];
  for (const f of data.files) {
    const res = await cache.match(f.key);
    if (res) files.push(new File([await res.blob()], f.name, { type: f.type }));
  }
  return { title: data.title, text: data.text, url: data.url, files };
}

async function clearShared() {
  await caches.delete('plana-share');
}

export function Share({ me }: { me: Me }) {
  const [shared, setShared] = useState<Shared | null | undefined>(undefined);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState(localStorage.getItem('plana-last-project') ?? '');
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    void readShared().then(setShared);
    void api<{ projects: Project[] }>('GET', '/api/projects').then((r) => {
      setProjects(r.projects);
      if (!r.projects.some((p) => p.id === projectId) && r.projects[0]) setProjectId(r.projects[0].id);
    });
  }, []);
  if (shared === undefined) return <p className="loading">Loading…</p>;
  if (shared === null) return <main className="share"><p>Nothing to add.</p><a href={href.projects()}>Projects</a></main>;
  return (
    <main className="share">
      <h1>Add to Spark</h1>
      <p>{[shared.title, shared.text, shared.url].filter(Boolean).join(' · ') || `${shared.files.length} photo(s)`}</p>
      <label>
        Project
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </label>
      <button disabled={!projectId || !!target} onClick={() => setTarget(projectId)}>Add</button>
      {target && <ShareInto me={me} projectId={target} shared={shared} />}
    </main>
  );
}

function ShareInto({ me, projectId, shared }: { me: Me; projectId: string; shared: Shared }) {
  const conn = useProject(projectId, me);
  const [error, setError] = useState('');
  const started = useRef(false);
  useEffect(() => {
    // A session that expired or lost access never reaches `synced` (Review Focus #2): the problem
    // check in the render below handles that. `started` keeps a flaky synced flicker mid-add from
    // re-running the whole thing and creating duplicate Spark cards.
    if (!conn?.synced || started.current) return;
    started.current = true;
    void (async () => {
      const link = shared.url || (shared.text.match(/https?:\/\/\S+/)?.[0] ?? '');
      if (link) createFromText(conn, projectId, inSpark, link);
      const words = [shared.title, shared.text.replace(link, '').trim()].filter(Boolean).join('\n');
      if (words && !link) createFromText(conn, projectId, inSpark, words);
      const errors: string[] = [];
      await createImages(conn, projectId, shared.files, () => inSpark, (m) => errors.push(m));
      await clearShared();
      if (errors.length) setError(errors.join(' '));
      else navigate(href.project(projectId, 'spark'));
    })();
  }, [conn?.synced]);
  // Signed-out or no-access: the shared payload is left in Cache Storage (never cleared) so
  // revisiting /#/share — picking a different project — still has it to add.
  if (conn?.problem) return <>{problemBanner(conn.problem)}</>;
  return error ? <p className="error" role="alert">{error}</p> : <p>Adding…</p>;
}
